import { useEffect, useState } from "react";
import { getRandomColor, isBlank } from "../../utils/annual-planner-helpers";

// Shared behind the add/edit phase template forms - the two modals only
// differ in their initial state and what happens on submit, everything else
// (state shape, add/remove/change a phase, the training/nutrition split,
// the "name + at least one filled phase" validation) is identical.
export const usePhaseTemplateForm = ({ getInitialState, onSubmit }) => {
  const [formState, setFormState] = useState(getInitialState);
  const [emptyPhasesState, setEmptyPhasesState] = useState(true);

  const { phase_template_name, phases } = formState;

  // Validate the form state and set the empty phases state
  useEffect(() => {
    const hasFilledPhase = phases.some(
      (phase) => !isBlank(phase.phaseName)
    );
    setEmptyPhasesState(!phase_template_name.trim() || !hasFilledPhase);
  }, [phases, phase_template_name]);

  // Handle changes to the form fields
  const handleFieldChange = (input, payload) => {
    setFormState((prev) => ({ ...prev, [input]: payload }));
  };

  // Handle changes to the phase fields
  const handlePhaseChange = (input, payload, index) => {
    setFormState((prev) => ({
      ...prev,
      phases: prev.phases.map((phase, i) =>
        i === index ? { ...phase, [input]: String(payload) } : phase
      ),
    }));
  };

  // Handle adding a new phase
  const handleAddPhase = (track) => {
    setFormState((prev) => ({
      ...prev,
      phases: [
        ...prev.phases,
        { phaseName: "", phaseColor: getRandomColor(), track },
      ],
    }));
  };

  // Handle removing a phase
  const handleRemovePhase = (index) => {
    setFormState((prev) => ({
      ...prev,
      phases: prev.phases.filter((_, i) => i !== index),
    }));
  };

  // Training and Nutrition are independent - a template only needs a name
  // and at least one filled phase in *either* track, not both. Empty rows
  // (e.g. a track's default blank row that never got used) are dropped here
  // rather than saved.
  const handleSubmit = (e) => {
    e.preventDefault();
    const filledPhases = phases.filter(
      (phase) => !isBlank(phase.phaseName)
    );
    if (!phase_template_name.trim() || !filledPhases.length) return;

    onSubmit({ phase_template_name, phases: filledPhases });
  };

  // Phases live in one flat array (mirrors the schema's own single list with
  // a `track` discriminator), so rows keep their real index into `phases`
  // even while grouped visually by track - handlePhaseChange/handleRemovePhase
  // never need to know about the split.
  const indexedPhases = phases.map((phase, i) => ({ phase, i }));
  const trainingRows = indexedPhases.filter(
    ({ phase }) => (phase.track || "training") === "training"
  );
  const nutritionRows = indexedPhases.filter(
    ({ phase }) => phase.track === "nutrition"
  );

  return {
    phase_template_name,
    trainingRows,
    nutritionRows,
    emptyPhasesState,
    handleFieldChange,
    handlePhaseChange,
    handleAddPhase,
    handleRemovePhase,
    handleSubmit,
  };
};
